import React, {createContext, useState, useEffect, useMemo, useCallback} from 'react';
import {Text, Tabs, TabList, Tab, TabPanels, TabPanel, Flex, Spacer, Button} from '@chakra-ui/react';

import Demographics from '../../components/Maintenance/Clients/Demographics';
import Groups from '../../components/Maintenance/Clients/Groups';
import Locations from '../../components/Maintenance/Clients/Locations';
import Arrangers from '../../components/Maintenance/Clients/Arrangers';
import {CustomDocs} from '../../components/Maintenance/Clients/CustomDocs';
import Emails from '../../components/Maintenance/Clients/Emails';
import {useTitle} from '../../components/General/Layout';
import {isAdmin} from '../../helpers/localStorage';
import LoadingWheel from '../../components/General/LoadingWheel';
import {Client} from '../../constants/client/types';
import {useQuery} from 'react-query';
import ClientMaintenanceSelect from '../../components/Client/ClientMaintenanceSelect';
import {get} from '../../service/ApiService';
import {Client as ClientUrls} from '../../service/Urls';

interface ClientContextData {
  client: Client | null;
  isAdding: boolean;
  selectedClientId: number | null;
  setSelectedClientId: (id: number | null) => void;
}

const ClientContext = createContext<ClientContextData>({client: null, isAdding: false, selectedClientId: null, setSelectedClientId: () => {}});

export const useClient = () => {
  const context = React.useContext(ClientContext);
  if (!context) {
    throw new Error('useData must be used within a <Parent />');
  }
  return context;
};

export default function ClientMaintenance() {
  const [selectedClientId, setSelectedClientId] = useState<number | null>(null);
  const [client, setClient] = useState<Client | null>(null);
  const [isAdding, setIsAdding] = useState(false);
  const [tabIndex, setTabIndex] = useState(0);
  const [_, setTitle] = useTitle();

  const isAdministrator = useMemo(() => isAdmin(), []);

  const canEdit = useMemo(() => client || isAdding, [client, isAdding]);

  const {isLoading} = useQuery(['client', selectedClientId], () => get(ClientUrls.CLIENT + '/' + selectedClientId), {
    enabled: selectedClientId !== null,
    onSuccess: data => {
      const formattedData = {
        ...data,
        clSince: data?.clSince?.substring(0, data?.clSince.indexOf('T')),
      };

      // Trim all string values
      for (const key in formattedData) {
        if (typeof formattedData[key] === 'string') {
          formattedData[key] = formattedData[key].trim();
        }
      }

      setClient(formattedData);
    },
    onError: () => {
      setClient(null);
    },
  });

  const handleTabChange = useCallback((index: number) => {
    setTabIndex(index);
  }, []);

  useEffect(() => {
    if (selectedClientId === null) {
      setClient(null);
    }
  }, [selectedClientId]);

  useEffect(() => {
    setTitle('Client Maintenance');

    if (selectedClientId != null) {
      setIsAdding(false);
    }
  }, [selectedClientId]);

  useEffect(() => {
    if (isAdding) {
      setTabIndex(0);
      setSelectedClientId(null);
    }
  }, [isAdding]);

  const value = useMemo(
    () => ({
      client,
      isAdding,
      selectedClientId,
      setSelectedClientId,
    }),
    [client, isAdding, selectedClientId]
  );

  return (
    <ClientContext.Provider value={value}>
      {/* If there's a heavy load for loading all the tabs at once we can add the prop isLazy to Tabs to load each tab separately when clicked */}
      <Tabs index={tabIndex} onChange={handleTabChange} variant="enclosed" mt="3" colorScheme="primary" isLazy>
        <TabList>
          <Tab>Demographics</Tab>
          <Tab isDisabled={!client} opacity={client ? 1 : 0.5}>
            Groups
          </Tab>
          <Tab isDisabled={!client} opacity={client ? 1 : 0.5}>
            Locations
          </Tab>
          <Tab isDisabled={!client} opacity={client ? 1 : 0.5}>
            Arrangers
          </Tab>
          {isAdministrator && (
            <Tab isDisabled={!client} opacity={client ? 1 : 0.5}>
              Custom Documents
            </Tab>
          )}
          <Tab isDisabled={!client} opacity={client ? 1 : 0.5}>
            Emails
          </Tab>
        </TabList>

        <Flex
          bg="graystone.primary"
          h={{xs: '30', sm: '10'}}
          px="4"
          py="2"
          mt="1"
          alignItems="center"
          flexDir={{base: 'column', lg: 'row'}}
          borderRadius="3"
        >
          <Text color="white">ClientId: {client?.clId ?? ''}</Text>
          <Spacer />
          <Text color="white">{client?.clCname ?? ''}</Text>
          <Spacer />
          <Flex alignItems="center" gap={2}>
            <ClientMaintenanceSelect />
            {isAdding ? (
              <Button colorScheme="red" onClick={() => setIsAdding(false)}>
                Cancel
              </Button>
            ) : (
              <Button onClick={() => setIsAdding(true)}>Add Client</Button>
            )}
          </Flex>
        </Flex>

        {isLoading ? (
          <LoadingWheel />
        ) : !canEdit ? (
          <></>
        ) : (
          <TabPanels>
            <TabPanel px="0">
              <Demographics />
            </TabPanel>
            <TabPanel px="0">
              <Groups />
            </TabPanel>
            <TabPanel px="0">
              <Locations />
            </TabPanel>
            <TabPanel px="0">
              <Arrangers />
            </TabPanel>
            {isAdministrator && (
              <TabPanel px="0">
                <CustomDocs />
              </TabPanel>
            )}
            <TabPanel px="0">
              <Emails />
            </TabPanel>
          </TabPanels>
        )}
      </Tabs>
    </ClientContext.Provider>
  );
}
